/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import moment from 'moment';
import { getCSS, getCSSVariableValue } from 'src/_metronic/assets/ts/_utils';
import { IPLoading } from 'src/components';
import { DSalesPerformaceParams, useChartList } from '../../services';

type Props = {
  chartData: any[];
  salesDay: DSalesPerformaceParams;
};

const SalesPerformance: React.FC<Props> = ({ chartData, salesDay }) => {
  const { isLoading, isFetching } = useChartList();
  const chartRef: any = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!chartRef.current || !chartData) {
      return;
    }

    const chart: any = new ApexCharts(chartRef.current, getChartOptions(chartData, salesDay, isFetching));

    if (chart && chartData) {
      setTimeout(() => {
        chart.render();
        var allLegends = document.querySelectorAll('#apexchartssales-performance-chart  .apexcharts-legend-series');
        for (var i = 0; i < allLegends.length; i++) {
          if (i === 1) chart.toggleSeries(allLegends[i].getAttribute('seriesname'));
        }
      }, 0);
    }

    return () => {
      if (chart && chartData) {
        chart.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, chartData]);

  return (
    <div ref={chartRef} id="kt_charts_widget_3_chart" style={{ height: '250px' }}>
      {isFetching && (
        <div className="card d-flex align-items-center bg-transparent w-100 shadow-none position-absolute bottom-0 mb-15 " style={{ zIndex: 99 }}>
          <div className="card-body">
            <IPLoading />
          </div>
        </div>
      )}
    </div>
  );
};

export { SalesPerformance };

function calculateMaxSalesPerHour(data: any) {
  let dailyMaxSales = [];
  let yesterdayMaxSales = [];
  let previousMax = 0;
  let previousYesterdayMax = 0; // Dünkü maksimum satış değerini tutmak için
  let currentHour = moment().hour();
  // Saat başına maksimum satışları hesapla
  for (let i = 0; i < 24; i++) {
    let maxDailySale = 0;
    let maxYesterdaySale = 0;

    // Veri setinde ilgili saatte günlük ve dünkü satışları bul
    const hourlyData = data.find((entry: any) => entry.hour === i);
    if (hourlyData) {
      if (hourlyData.daily_sale !== null) {
        maxDailySale = parseFloat(hourlyData.daily_sale);
      }
      if (hourlyData.yesterday_sale !== null) {
        maxYesterdaySale = parseFloat(hourlyData.yesterday_sale);
      }
    }

    // Eğer günlük satış verisi mevcut değilse, önceki saatlerdeki maksimum satışı kullan
    if (maxDailySale === 0) {
      maxDailySale = previousMax;
    } else {
      previousMax = maxDailySale;
    }

    // Dünkü satış verisi mevcut değilse, önceki günün aynı saatine ait veriyi kullan
    if (maxYesterdaySale === 0) {
      maxYesterdaySale = previousYesterdayMax;
    } else {
      previousYesterdayMax = maxYesterdaySale;
    }

    if (i <= currentHour) {
      dailyMaxSales.push(maxDailySale);
    } else {
      dailyMaxSales.push(null);
    }
    yesterdayMaxSales.push(maxYesterdaySale);
  }

  return { dailyMaxSales, yesterdayMaxSales };
}
function calculateMaxSalesDay(data: any) {
  let firstMaxSales = [];
  let secondMaxSales = [];
  let previousMax = 0;
  let previousYesterdayMax = 0; // Dünkü maksimum satış değerini tutmak için

  // Gün başına maksimum satışları hesapla
  for (let i = 0; i < data.length; i++) {
    // Haftanın 7 günü olduğunu varsayalım
    let maxFirstSale = 0;
    let maxSecondSale = 0;

    // Veri setinde ilgili günde günlük ve dünkü satışları bul
    const dailyData = data.find((entry: any) => entry.day === i + 1); // Günler 1'den başlıyor gibi görünüyor
    if (dailyData) {
      if (dailyData.first !== null) {
        maxFirstSale = parseFloat(dailyData.first);
      }
      if (dailyData.second !== null) {
        // yesterday_sale yerine second
        maxSecondSale = parseFloat(dailyData.second);
      }
    }

    // Eğer günlük satış verisi mevcut değilse, önceki günlerdeki maksimum satışı kullan
    if (maxFirstSale === 0) {
      maxFirstSale = previousMax;
    } else {
      previousMax = maxFirstSale;
    }

    // Dünkü satış verisi mevcut değilse, önceki günün aynı saatine ait veriyi kullan
    if (maxSecondSale === 0) {
      maxSecondSale = previousYesterdayMax;
    } else {
      previousYesterdayMax = maxSecondSale;
    }

    // Maksimum satışları listelere ekle
    firstMaxSales.push(maxFirstSale);
    secondMaxSales.push(maxSecondSale);
  }

  return { firstMaxSales, secondMaxSales };
}
function getChartOptions(chartData: any, salesDay: DSalesPerformaceParams, isFetching: boolean): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500');
  const borderColor = getCSSVariableValue('--bs-gray-200');
  const baseLightColor = getCSSVariableValue('--bs-info');
  const baseYesterdayColor = getCSSVariableValue('--bs-warning');
  const lightDailyColor = getCSSVariableValue('--bs-info');
  const lightYesterdayColor = getCSSVariableValue('--bs-warning');
  const { dailyMaxSales, yesterdayMaxSales } = calculateMaxSalesPerHour(chartData);
  const { firstMaxSales, secondMaxSales } = calculateMaxSalesDay(chartData);
  let series: any = [];
  let labels: any;
  if (salesDay === 'daily') {
    labels = Array.from({ length: 24 }, (_, i) => {
      const hour = String(i).padStart(2, '0');
      const minute = '00';
      return `${hour}:${minute}`;
    });
    series = [
      {
        name: 'Bugün',
        data: dailyMaxSales,
      },
      {
        name: 'Dün',
        data: yesterdayMaxSales,
      },
    ];
  } else if (salesDay === 'sevenDays' || salesDay === 'thirtyDays') {
    labels = chartData?.map((entry: any) => entry?.day) || [];
    series =
      salesDay === 'sevenDays'
        ? [
            {
              name: '7 Gün',
              data: firstMaxSales,
            },
            {
              name: '7 Gün Önceki',
              data: secondMaxSales,
            },
          ]
        : [
            {
              name: '30 Gün',
              data: firstMaxSales,
            },
            {
              name: 'Son 30 Gün',
              data: secondMaxSales,
            },
          ];
  }

  return {
    series: series,
    chart: {
      id: 'sales-performance-chart',
      fontFamily: 'inherit',
      type: 'area',
      height: 250,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: !isFetching,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 0.2,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseLightColor, baseYesterdayColor],
    },
    xaxis: {
      categories: labels,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '11.1px',
        },
        formatter: function (value, timestamp) {
          if (salesDay === 'sevenDays' || salesDay === 'thirtyDays') {
            return value;
          } else {
            const index = labels.indexOf(value);
            if (index % 3 === 0 || value === '23:00') {
              return value;
            } else {
              return '';
            }
          }
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseLightColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: false,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (val) {
          return Number(val)?.toLocaleString('tr-TR') + ' TL';
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = w.globals.categoryLabels[dataPointIndex];
        let firstSaleValue;
        let secondSaleValue;
        if (salesDay === 'sevenDays' || salesDay === 'thirtyDays') {
          firstSaleValue = firstMaxSales[dataPointIndex];
          secondSaleValue = secondMaxSales[dataPointIndex];
        } else {
          firstSaleValue = dailyMaxSales[dataPointIndex];
          secondSaleValue = yesterdayMaxSales[dataPointIndex];
        }
        const data = [firstSaleValue, secondSaleValue];
        const seriesNames = w.globals.seriesNames;

        const colorSwatches = w.globals.colors
          ?.map((color: any, index: number) =>
            series[index]?.length > 0
              ? `<div class="d-flex align-items-center mt-1"><div style="background-color: ${color}; width: 7px; height: 7px; border-radius:7px;"></div><span class="mx-3 fs-7">${
                  seriesNames[index]
                } - <span class="fw-bolder">${data[index] ? data[index]?.toLocaleString('tr-TR') + ' TL' : '0 TL'}</span></span></div>`
              : '',
          )
          ?.join('');

        var formattedValue;
        if (String(value)?.includes(':') || value === '') {
          if (value === '') {
            const hour = String(dataPointIndex).padStart(2, '0');
            const minute = '00';
            formattedValue = `00:00 - ${hour}:${minute}`;
          } else {
            const timeParts = value.split(':');
            const hour = timeParts[0].padStart(2, '0');
            const minute = timeParts[1].padStart(2, '0');
            formattedValue = `00:00 - ${hour === '23' ? `${hour}:${59}` : `${hour}:${minute}`} `;
          }
        } else {
          formattedValue = `${value}. Gün`;
        }

        return `<div class="my-custom-tooltip">
                    <div class="bg-secondary p-2">
                      <span class="data-value fs-7">${formattedValue}</span>
                    </div>
                    <div class="px-4 py-1">
                      ${colorSwatches}
                    </div>
                  </div>`;
      },
    },
    colors: [lightDailyColor, lightYesterdayColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: [baseLightColor, baseYesterdayColor],
      strokeWidth: 3,
    },
  };
}
